import { INSIGHT } from '../insights/insight-gate/insight-rules.config';
import { inject, Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import {
  concat,
  concatMap,
  defaultIfEmpty,
  defer,
  EMPTY,
  map,
  Observable,
  of,
  switchMap,
  take,
} from 'rxjs';

import { EffectsBase } from '@ps/core-data-access';
import { AuthSelectorsFacade } from '@ps/auth-data-access';
import { APP_ROUTES } from '@ps/core-config';
import { ToastService } from '@ps/core-overlay';
import { SURVEY_STATUS } from '@ps/survey-kernel';
import {
  MC_GROUPED_MESSAGES,
  Survey,
  SurveyKind,
  trafficChannelParentId,
} from '@ps/survey-utils';

import { SurveyCreationInsightsActions } from '../insights';
import { InsightGate } from '../insights/insight-gate/insight-gate.service';

import {
  SurveyCreationCoreActions,
  SurveyCreationCoreApiActions,
} from './survey-creation-core.actions';
import {
  SurveyCreationLaunchActions,
  SurveyCreationLaunchApiActions,
} from '../launch/survey-creation-launch.actions';
import {
  SurveyCreationAudienceApiActions,
  SurveyCreationMcApiActions,
  SurveyCreationTrackerApiActions,
} from '../audience/survey-creation-audience.actions';
import {
  DEFAULT_SAVE_GATE,
  SURVEY_NOT_READY_TO_LAUNCH_MESSAGE,
} from './utils/survey-save-pipeline.helper';
import { routableSurveyId } from './utils/survey-route.helper';
import { activeChildNavigationExtras } from './utils/active-child-navigation.helper';
import { selectActiveChildKey } from './survey-creation-core.selectors';
import { SurveyPersistenceService } from '../../services/save/survey-persistence.service';
import { SurveySavePipeline } from './pipeline/survey-save.pipeline';
import {
  specificAudienceExitNavigation,
  specificAudienceReturnOf,
} from './utils/specific-audience-exit.helper';

// Owns the save/clone lifecycle for a standard survey — save is dispatched
// from create, edit and launch alike, so it lives in core/ (unlike creation,
// which is create-only and lives in SurveyAudienceCreateEffects in audience/;
// both share the stage-0 read via survey-save-pipeline.helper.ts).
@Injectable()
export class SurveyCreationSaveEffects extends EffectsBase {
  private readonly _store = inject(Store);
  private readonly _actions$ = inject(Actions);
  private readonly _insightGate = inject(InsightGate);

  private readonly _toastService = inject(ToastService);
  private readonly _router = inject(Router);

  private readonly _persistence = inject(SurveyPersistenceService);
  private readonly _authSelectors = inject(AuthSelectorsFacade);
  private readonly _savePipeline = inject(SurveySavePipeline);

  // Only a plain save stays on the page, so only it refreshes insights.
  public readonly saveStandardSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveSurvey),
      this._savePipeline.save(() =>
        of(SurveyCreationInsightsActions.refreshAfterSave()).pipe(
          this._insightGate.onlyIfAllowed(INSIGHT.SAVE_REFRESH),
        ),
      ),
    ),
  );

  public readonly saveAndCreateNewSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveAndCreateNew),
      this._savePipeline.save(() => this._navigateTo(APP_ROUTES.CREATE_SURVEY)),
    ),
  );

  public readonly saveSurveyAndNavigateToLaunchPage$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveAndLaunch),
      this._savePipeline.save((survey) =>
        this._navigateWithActiveChild(
          APP_ROUTES.launchSurvey(routableSurveyId(survey)),
          survey,
        ),
      ),
    ),
  );

  // A Specific Audience channel goes live in the same write as its audience,
  // then the buyer is taken back to the Traffic Channels drawer it came from.
  public readonly saveSurveyAsLive$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveAsLive),
      this._savePipeline.save(
        (survey) => {
          this._router.navigate(
            ...specificAudienceExitNavigation(
              trafficChannelParentId(survey),
              specificAudienceReturnOf(this._router),
            ),
          );
          return EMPTY;
        },
        DEFAULT_SAVE_GATE,
        { status: SURVEY_STATUS.LIVE },
      ),
    ),
  );

  public readonly saveAndEditAudienceSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveAndEditAudience),
      this._savePipeline.save((survey) =>
        this._navigateWithActiveChild(
          APP_ROUTES.editSurvey(routableSurveyId(survey)),
          survey,
        ),
      ),
    ),
  );

  public readonly saveAndOpenManageSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveAndOpenManageSurvey),
      this._savePipeline.save((survey) =>
        this._navigateTo(APP_ROUTES.manageSurvey(routableSurveyId(survey))),
      ),
    ),
  );

  // Redirect trigger is scoped to this action alone, not bare saveSurveySuccess —
  // every other save variant shares that action, so wiring off it directly would
  // make the ordinary page Save button also pop a test-link tab.
  public readonly saveAndTestSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveAndTest),
      this._savePipeline.save(() =>
        of(SurveyCreationLaunchApiActions.testEntryLinkReady()),
      ),
    ),
  );

  public readonly saveAndCloneSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveAndCloneSurvey),
      this._savePipeline.save((survey) => this._cloneSurvey(survey)),
    ),
  );

  // Saves, then hands off to SurveyCreationLaunchSubmitEffects for the status
  // change. The save gate drops an invalid survey without emitting (its toasts
  // already say why), so defaultIfEmpty ends the launch attempt explicitly.
  public readonly launchSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationLaunchActions.launchSurvey),
      concatMap((action) =>
        of<Action>(action).pipe(
          this._savePipeline.save(
            (survey) =>
              of(
                SurveyCreationLaunchApiActions.changeLaunchStatus({
                  surveyId: survey.id,
                  status: action.status,
                  pricingConfirmationMessage: action.pricingConfirmationMessage,
                }),
              ),
            { alwaysGateLaunchForm: true },
          ),
          defaultIfEmpty<Action, Action>(
            SurveyCreationLaunchApiActions.launchSurveyFailure({
              error: SURVEY_NOT_READY_TO_LAUNCH_MESSAGE,
            }),
          ),
        ),
      ),
    ),
  );

  // Only announces the save succeeded — SurveyCreationWorkflowExitEffects owns
  // the navigate, same split as the impersonation-exit variants below.
  public readonly saveAndExitSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveAndExit),
      this._savePipeline.save((survey) =>
        of(SurveyCreationCoreApiActions.saveSurveyAndExitSuccess({ survey })),
      ),
    ),
  );

  // Only announces the save succeeded — SurveyCreationWorkflowExitEffects owns
  // what happens next (disconnecting, navigating). Keeps this class's one job
  // "run the save pipeline" regardless of why a caller wanted the exit.
  public readonly saveSurveyAndDisconnectImpersonation$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveSurveyAndDisconnectImpersonation),
      this._savePipeline.save((survey) =>
        of(
          SurveyCreationCoreApiActions.saveSurveyAndDisconnectImpersonationSuccess(
            { survey },
          ),
        ),
      ),
    ),
  );

  // Same reasoning as above; the target buyer isn't carried through the
  // pipeline's own stages, so it's closed over per-emission here instead.
  public readonly saveSurveyAndSwitchImpersonatedBuyer$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveSurveyAndSwitchImpersonatedBuyer),
      this._savePipeline.saveWithTrigger(
        (action) => (survey) =>
          of(
            SurveyCreationCoreApiActions.saveSurveyAndSwitchImpersonatedBuyerSuccess(
              { survey, buyer: action.buyer },
            ),
          ),
      ),
    ),
  );

  // Same reasoning as above; routeKey/params aren't carried through the
  // pipeline's own stages, so they're closed over per-emission here instead.
  // Only announces the save succeeded — SurveyCreationWorkflowExitEffects owns
  // performing the actual switch, same split as the other save-and-X variants.
  public readonly saveSurveyAndSwitchToLegacy$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationCoreActions.saveSurveyAndSwitchToLegacy),
      concatMap(({ routeKey, params }) =>
        of<Action>(
          SurveyCreationCoreActions.saveSurveyAndSwitchToLegacy({
            routeKey,
            params,
          }),
        ).pipe(
          this._savePipeline.save((survey) =>
            of(
              SurveyCreationCoreApiActions.saveSurveyAndSwitchToLegacySuccess({
                survey,
                routeKey,
                params,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  // Reset before navigating so the edit page loads fresh data, not stale
  // state. concat + defer guarantees that order — tap-then-map would only
  // work by accident of Router deferring navigation to a microtask.
  public readonly navigateToEditSurvey$ = createEffect(() =>
    this._actions$.pipe(
      ofType(
        SurveyCreationAudienceApiActions.createStandardSurveySuccess,
        SurveyCreationCoreApiActions.cloneSurveySuccess,
        SurveyCreationAudienceApiActions.createSurveyFromTemplateSuccess,
        SurveyCreationMcApiActions.createMcTraditionalSurveysSuccess,
        SurveyCreationMcApiActions.createMcGroupedProjectSuccess,
        SurveyCreationTrackerApiActions.createTrackerProjectSuccess,
      ),
      switchMap(({ survey }) =>
        concat(
          of(SurveyCreationCoreActions.reset()),
          defer(() => this._navigateTo(APP_ROUTES.editSurvey(survey.id))),
        ),
      ),
    ),
  );

  // Emits cloneSurveySuccess/Failure so navigateToEditSurvey$ (and any other listener) reacts
  // the same way regardless of the caller.
  //
  // A Group MC has already been persisted by the time this runs — only the
  // clone itself is unsupported, so it reports that and contributes no action
  // rather than failing the save the buyer just completed.
  private _cloneSurvey(survey: Survey): Observable<Action> {
    if (SurveyKind.isGroupMc(survey)) {
      this._toastService.info(MC_GROUPED_MESSAGES.CLONE_UNAVAILABLE);
      return EMPTY;
    }

    return this._persistence
      .clone(survey.id, this._authSelectors.hasImpersonatedBuyer())
      .pipe(
        map((clonedSurvey) => {
          this._toastService.success('Survey successfully cloned', {
            keepOpen: false,
          });
          return SurveyCreationCoreApiActions.cloneSurveySuccess({
            survey: clonedSurvey,
          });
        }),
        this.psCatchError(
          (error) =>
            of(
              SurveyCreationCoreApiActions.cloneSurveyFailure({
                error: error.message,
              }),
            ),
          { notify: true },
        ),
      );
  }

  // Read once the save has settled, so it is the child the save left active.
  private _navigateWithActiveChild(
    route: string,
    survey: Survey,
  ): Observable<never> {
    return this._store.select(selectActiveChildKey).pipe(
      take(1),
      switchMap((activeChildKey) =>
        this._navigateTo(
          route,
          activeChildNavigationExtras(survey, activeChildKey),
        ),
      ),
    );
  }

  // Navigates as a side effect and contributes no action to the stream.
  private _navigateTo(
    route: string,
    extras?: NavigationExtras,
  ): Observable<never> {
    this._router.navigate([route], extras);
    return EMPTY;
  }
}
